import type { MaterialsFolder, MaterialsItem } from "@/lib/materials-api";

export type MaterialsBulkDestroyFoldersResult = {
  deleted: { id: number; name: string }[];
  blocked: {
    id: number;
    name: string;
    reason: string;
    message?: string;
  }[];
  failed: { id: number; code?: string; message?: string }[];
};

/** 文件夹是否为空（无子夹、无文件） */
export function folderIsEmpty(
  folderId: number,
  folders: MaterialsFolder[],
  items: MaterialsItem[],
): boolean {
  const hasChildFolder = folders.some((f) => f.parent_id === folderId);
  if (hasChildFolder) return false;
  return !items.some((item) => item.folder_id === folderId);
}

export function summarizeBulkDeleteFolders(
  result: MaterialsBulkDestroyFoldersResult,
): { tone: "success" | "warning" | "error"; message: string } {
  return summarizeMixedBulkDelete({
    itemsDeleted: 0,
    itemsBlocked: 0,
    itemsFailed: 0,
    foldersDeleted: result.deleted.length,
    foldersBlocked: result.blocked.length,
    foldersFailed: result.failed.length,
  });
}

/** 多选统一删除：文件 + 文件夹结果汇总文案 */
export function summarizeMixedBulkDelete(counts: {
  itemsDeleted: number;
  itemsBlocked: number;
  itemsFailed: number;
  foldersDeleted: number;
  foldersBlocked: number;
  foldersFailed: number;
}): { tone: "success" | "warning" | "error"; message: string } {
  const deleted = counts.itemsDeleted + counts.foldersDeleted;
  const blocked = counts.itemsBlocked + counts.foldersBlocked;
  const failed = counts.itemsFailed + counts.foldersFailed;

  if (deleted === 0 && blocked === 0 && failed > 0) {
    return { tone: "error", message: "删除失败，请稍后重试" };
  }
  if (deleted === 0 && blocked > 0) {
    const parts: string[] = [];
    if (counts.foldersBlocked > 0) {
      parts.push(`${counts.foldersBlocked} 个文件夹不为空`);
    }
    if (counts.itemsBlocked > 0) {
      parts.push(`${counts.itemsBlocked} 个素材仍被引用`);
    }
    return {
      tone: "warning",
      message: `${parts.join("，")}，已跳过`,
    };
  }
  if (deleted > 0 && (blocked > 0 || failed > 0)) {
    const parts = [`已删除 ${deleted} 项`];
    if (blocked > 0) parts.push(`${blocked} 项已跳过`);
    if (failed > 0) parts.push(`${failed} 项失败`);
    return { tone: "warning", message: parts.join("，") };
  }
  return { tone: "success", message: `已删除 ${deleted} 项` };
}
